import { useAtomValue } from "jotai";
import { useCallback, useEffect, useRef, useState } from "react";
import { getMockVesselEvaluationDashboard } from "../mocks/vessel-evaluation.mocks";
import type {
  VesselEvaluationDashboard,
  VesselEvaluationDashboardRequest,
} from "../models/vessel-evaluation.models";
import {
  getVesselEvaluationDashboard,
  getVesselEvaluationErrorMessage,
} from "../services/vessel-evaluation.service";
import { vesselEvaluationMockEnabledAtom } from "./vessel-evaluation.atoms";

// Owns source selection and cancels stale requests when the route, refresh, or sidebar toggle changes.
export const useVesselEvaluationDashboard = (request: VesselEvaluationDashboardRequest) => {
  const mockEnabled = useAtomValue(vesselEvaluationMockEnabledAtom);
  const [dashboard, setDashboard] = useState<VesselEvaluationDashboard | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshSequence, setRefreshSequence] = useState(0);
  const previousMockEnabled = useRef(mockEnabled);

  const refreshDashboard = useCallback(
    () => setRefreshSequence((current) => current + 1),
    [],
  );

  useEffect(() => {
    const controller = new AbortController();
    let isCurrentRequest = true;

    const loadDashboard = async () => {
      if (previousMockEnabled.current !== mockEnabled) {
        previousMockEnabled.current = mockEnabled;
        setDashboard(null);
      }

      setLoading(true);
      setError(null);

      try {
        const nextDashboard = mockEnabled
          ? getMockVesselEvaluationDashboard(request)
          : await getVesselEvaluationDashboard(request, controller.signal);

        if (isCurrentRequest) {
          setDashboard(nextDashboard);
        }
      } catch (requestError) {
        if (isCurrentRequest && !controller.signal.aborted) {
          setDashboard(null);
          setError(getVesselEvaluationErrorMessage(requestError));
        }
      } finally {
        if (isCurrentRequest) {
          setLoading(false);
        }
      }
    };

    void loadDashboard();

    return () => {
      isCurrentRequest = false;
      controller.abort();
    };
  }, [
    mockEnabled,
    refreshSequence,
    request,
  ]);

  return { dashboard, error, loading, mockEnabled, refreshDashboard };
};
